import { useCallback, useEffect, useState } from 'react';
import {
    install as installIntercom,
    boot as bootIntercom
} from '@sbu/utils.intercom';
import { DynamicFeatureFlag, useFeatureFlags } from '@sbu/hooks.featureflag';
import { useCurrentUserSumo } from '@sbu/hooks.shared';

const useIntercomInitialize = () => {
    const [intercomInitialized, setIntercomInitialized] = useState(false);
    const { hasAll: hasIntercomEnabled } = useFeatureFlags({
        requestedFlags: [DynamicFeatureFlag.UIKansoIntercomWidgetOverride],
        skip: false,
        useV2: true
    });
    const { hasAll: hasUserIntercomSettings } = useFeatureFlags({
        requestedFlags: [DynamicFeatureFlag.UIUserIntercomSettings],
        skip: false,
        useV2: true
    });
    const { data: currentUser } = useCurrentUserSumo({ enabled: hasIntercomEnabled });

    const initializeIntercom = useCallback(() => {
        if (hasIntercomEnabled && currentUser && !intercomInitialized) {
            try {
                const createTs = moment
                    .utc(currentUser.userCreationTime, moment.ISO_8601)
                    .unix();
                const sumoUserId = padZeros(toHex(currentUser.id));
                const userId = hasUserIntercomSettings
                    ? currentUser.userIdForIntercom
                    : `${sumoUserId}-${deployment}`;
                const userHash = hasUserIntercomSettings
                    ? { user_hash: currentUser.userHashForIntercom }
                    : {};

                const intercomConfig = {
                    app_id: 'gt2g7vge',
                    name: currentUser.firstName + ' ' + currentUser.lastName,
                    email: currentUser.name,
                    created_at: createTs,
                    user_id: userId,
                    ...userHash,
                    sumo_user_id: sumoUserId,
                    sumo_org_id: currentUser.organizationId
                        .toString(16)
                        .padStart(16, '0'),
                    sumo_org: currentUser.organization,
                    sumo_account_type: currentUser.accountType,
                    sumo_deployment: deployment,
                    sumo_account_status: currentUser.accountStatus,
                    company: {
                        id: currentUser.organizationId
                            .toString(16)
                            .padStart(16, '0'),
                        name: currentUser.organization,
                        plan: currentUser.accountType
                    }
                };

                bootIntercom(intercomConfig);
                setIntercomInitialized(true);
            } catch (error) {
                console.error('Error initializing Intercom:', error);
            }
        }
    }, [
        currentUser,
        intercomInitialized,
        hasIntercomEnabled,
        setIntercomInitialized
    ]);

    useEffect(() => {
        if (!intercomInitialized) {
            installIntercom().then(() => {
                initializeIntercom();
            });
        }
    }, [intercomInitialized]);

    return intercomInitialized;
};

export default useIntercomInitialize;
